import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { useAuth } from '../contexts/AuthContext'
import { ActiveCallBar } from './ActiveCallBar'
import { telephonyApi, type PendingWrapup } from './api'
import { CallState, callMachine } from './callMachine'
import { TelephonyDeviceStatus, telephonyDevice } from './device'
import { isTelephonyV2Enabled } from './flags'
import { IncomingCallOverlay } from './IncomingCallOverlay'
import { outcomeQueue } from './outcomeQueue'
import { planOutcomeSubmission, type QualifiedCall } from './outcomeSubmission'
import { PendingOutcomeBanner } from './PendingOutcomeBanner'
import { normalizePhoneToE164 } from './phone'
import { telephonySocket } from './socket'
import { readTenantPaused } from './tenantPause'
import { useCallEndRefresh } from './useCallEndRefresh'

/**
 * Provider téléphonie v2 — monté UNE fois au-dessus du shell applicatif.
 *
 * Ne fait RIEN si le flag `telephony_v2` est absent : aucun Device, aucune
 * socket, aucun écouteur, aucune écriture de disponibilité. Les utilisateurs
 * v1 ne sont pas affectés — et surtout, il n'y a jamais deux Devices Twilio
 * enregistrés dans la même session (sinon : double sonnerie).
 *
 * Remplace, pour les utilisateurs v2 : `globalTwilioListener` +
 * `CallStateContext` + les états locaux des pages softphone/dialer.
 *
 * Responsabilités :
 * - cycle de vie du Device et de la socket ;
 * - ORCHESTRATION des actions d'appel (composer, décrocher, raccrocher,
 *   qualifier) — les pages ne font qu'appeler ces actions et lire l'état ;
 * - disponibilité de l'agent alignée sur l'état RÉEL du poste (F-5) ;
 * - présentation des entrants sur TOUTE page (le Device est global).
 */

export interface DialParams {
  /** Numéro tel que saisi ou stocké — normalisé en E.164 avant tout envoi. */
  phoneNumber: string
  contactId?: string
  contactName?: string
  campaignId?: string
  campaignContactId?: string
}

export type DialFailure =
  /** Flag v2 absent : le provider est inerte, aucun poste n'est enregistré. */
  | 'disabled'
  /** Numéro non normalisable : rien n'a été tenté. */
  | 'invalid-number'
  /** Le poste n'est pas au repos (appel en cours, entrant qui sonne). */
  | 'busy'
  /** Le serveur a refusé de créer le Call : AUCUN appel Twilio n'est parti. */
  | 'create-failed'
  /**
   * LOT-INDICATIFS-FIX-2 : le serveur a refusé la DESTINATION — numéro hors
   * du plan de numérotation de son pays (400 `INVALID_DESTINATION`, PR #129).
   * Rien n'a été composé, aucun `Call` n'existe. Le refus porte le diagnostic
   * du serveur (`message`, `hint`) — cf. `DialRefusal`.
   */
  | 'invalid-destination'
  /**
   * PAUSE-PLATEAU (2026-10-08) : le serveur a refusé parce que le PLATEAU est
   * en pause (403 `TENANT_PAUSED`, lu en base). Rien n'a été composé, aucun
   * `Call` n'existe. Le provider passe aussitôt `tenantPaused` à `true` : la
   * scène affiche le bandeau « Plateau en pause », jamais un écran cassé.
   */
  | 'tenant-paused'
  /** Le Call existe mais le poste n'a pas pu établir la connexion (Call annulé). */
  | 'connect-failed'

/**
 * Échec d'une tentative d'appel.
 *
 * L'échec porte un CODE — jamais une phrase fabriquée ici. Le provider
 * fabriquait des messages en français en dur (« Un appel est déjà en cours »,
 * « Numéro invalide »…) que la scène affichait tels quels : le mode anglais
 * restait troué exactement là où l'agent a besoin de comprendre. La traduction
 * se fait dans la scène, via `dialError.<reason>` du namespace `telephony`.
 *
 * UNE exception, assumée (LOT-INDICATIFS-FIX-2) : `invalid-destination`
 * transporte le `message` et le `hint` du SERVEUR. Ce message est un
 * DIAGNOSTIC propre au numéro (pays reconnu, indicatif saisi deux ou trois
 * fois…) qu'aucune clé statique ne peut rendre ; la scène l'affiche en
 * priorité et ne retombe sur `dialError.invalid-destination` qu'en son
 * absence. Le serveur parle français — limite connue du mode anglais.
 */
export interface DialRefusal {
  ok: false
  reason: DialFailure
  /** Diagnostic du serveur (français) — `invalid-destination` seulement. */
  message?: string
  /** Conseil de saisie du serveur — `invalid-destination` seulement. */
  hint?: string
}

/** Résultat d'une tentative d'appel. */
export type DialResult = { ok: true; callId: string } | DialRefusal

/**
 * Appel PRÉPARÉ mais pas encore composé : le `Call` existe en base, aucun leg
 * Twilio n'a été demandé. C'est l'objet du PRÉ-LANCEMENT (cf. `prelaunch.ts`).
 */
export interface PreparedCall {
  callId: string
  /** Numéro DÉJÀ normalisé en E.164 — plus aucune conversion au moment de composer. */
  phoneNumber: string
  contactId?: string
  contactName?: string
  campaignId?: string
  campaignContactId?: string
}

export type PrepareResult = { ok: true; prepared: PreparedCall } | DialRefusal

/**
 * LOT-INDICATIFS-FIX-2 — lit le refus de DESTINATION du serveur dans l'erreur
 * HTTP de la création d'appel : 400 dont le corps porte
 * `code: 'INVALID_DESTINATION'` (contrat de la PR #129, préservé par le filtre
 * HTTP global). Toute autre erreur rend `null` — elle reste un `create-failed`.
 *
 * Typage en canard sur la forme axios (`error.response.{status,data}`) : le
 * provider ne dépend pas d'axios, et une erreur réseau (sans `response`) ne
 * doit pas passer pour un refus de destination.
 */
/**
 * PAUSE-PLATEAU — lit le refus « plateau en pause » du serveur : 403 dont le
 * corps porte `code: 'TENANT_PAUSED'` (contrat de `telephony/tenant-pause.ts`,
 * préservé par le filtre HTTP global). Toute autre 403 (gating d'essai…) ou
 * toute autre erreur rend `false` : elle reste un `create-failed`.
 */
export { readTenantPaused } from './tenantPause'

/** Période de relecture de l'état du plateau (au plus 60 s, exigence du lot). */
export const TENANT_OPTIONS_POLL_MS = 60_000

export function readInvalidDestination(
  error: unknown,
): { message?: string; hint?: string } | null {
  const response = (
    error as { response?: { status?: unknown; data?: unknown } } | null
  )?.response
  if (!response || response.status !== 400) return null
  const data = response.data as
    | { code?: unknown; message?: unknown; hint?: unknown }
    | null
    | undefined
  if (!data || data.code !== 'INVALID_DESTINATION') return null
  return {
    message: typeof data.message === 'string' ? data.message : undefined,
    hint: typeof data.hint === 'string' ? data.hint : undefined,
  }
}

/**
 * Code et message d'une erreur du SDK Twilio Voice (`TwilioError` : `code`
 * numérique, `message`) ou d'une erreur DOM (`NotAllowedError`…), sous la
 * forme attendue par `POST /telephony/calls/:id/cancel` (issue #144).
 * `undefined` quand rien d'exploitable : l'annulation garde son corps vide.
 */
export function readSdkError(
  error: unknown,
): { code?: string; message?: string } | undefined {
  const raw = (error ?? {}) as { code?: unknown; name?: unknown; message?: unknown }
  const code =
    typeof raw.code === 'number' && Number.isFinite(raw.code)
      ? String(raw.code)
      : typeof raw.code === 'string' && raw.code.trim()
        ? raw.code.trim()
        : typeof raw.name === 'string' && raw.name.trim()
          ? raw.name.trim()
          : undefined
  const message =
    typeof raw.message === 'string' && raw.message.trim()
      ? raw.message.trim().slice(0, 500)
      : undefined
  if (!code && !message) return undefined
  return { ...(code ? { code } : {}), ...(message ? { message } : {}) }
}

export interface TelephonyApi {
  /** Flag actif pour cette session. Faux ⇒ toutes les actions sont inertes. */
  enabled: boolean
  dial(params: DialParams): Promise<DialResult>
  /**
   * ÉTAPE 1 SEULE du sortant : créer le `Call`, sans composer. Sert au
   * pré-lancement de l'auto-dial — l'aller-retour HTTP est payé pendant que
   * le rebours descend, jamais au moment où l'agent attend la sonnerie.
   */
  prepareCall(params: DialParams): Promise<PrepareResult>
  /** ÉTAPE 2 : composer un appel préparé. La sonnerie part ICI, pas avant. */
  dialPrepared(prepared: PreparedCall): Promise<DialResult>
  /**
   * Renonce à un appel préparé (Pause/Stop pendant la fenêtre) : le `Call` est
   * ANNULÉ côté serveur, il ne deviendra jamais un appel.
   */
  discardPrepared(prepared: PreparedCall): void
  /**
   * RAPPEL D'UN LEG EN VOL — Stop/Pause pendant la fenêtre de connexion
   * anticipée. Raccroche SILENCIEUSEMENT le leg (aucun wrapup) et supprime le
   * `Call` côté serveur, en CONSERVANT la réservation du contact.
   *
   * @returns `true` si un leg a réellement été rappelé.
   */
  abortLaunchedCall(params: { callId: string; countdownSeconds: number }): boolean
  acceptIncoming(): void
  rejectIncoming(): void
  hangup(): void
  toggleMute(): void
  sendDigits(digits: string): void
  /** Volume d'ÉCOUTE du correspondant, 0 → 1. Mémorisé d'un appel à l'autre. */
  setVolume(level: number): void
  /**
   * Issue #190 — « Activer le micro » / « J'ai autorisé, réessayer ». À
   * appeler depuis un CLIC : demande (ou redemande) le micro puis met le poste
   * en service. Ne rejette jamais : l'issue se lit dans `useDeviceStatus()`,
   * la promesse ne sert qu'à savoir quand la tentative est terminée.
   */
  requestMicrophone(): Promise<void>
  /**
   * QUALIFIE l'appel en wrapup — **sans attendre le réseau**.
   *
   * Le poste est libéré IMMÉDIATEMENT (la machine repasse au repos, donc le
   * contact suivant — déjà chargé et verrouillé — s'affiche dans la foulée) et
   * l'écriture part en arrière-plan via `outcomeQueue`. Un échec ne bloque
   * plus rien : il s'annonce dans un bandeau avec un bouton « Réessayer ».
   *
   * Rend `void` : il n'y a plus de verdict à attendre. C'est le changement de
   * contrat central de SCENE-2B.
   */
  submitOutcome(params: {
    /**
     * L'appel à qualifier, tel que la scène l'a figé à l'entrée en wrapup.
     * `null` ⇒ repli sur l'instantané courant (chemins qui n'ont pas d'entrée
     * en wrapup identifiable).
     */
    call?: QualifiedCall | null
    outcome: string
    notes?: string
    scheduledCallbackTime?: string
    /**
     * LOT-SCÈNE-LINK : contact créé (ou repris) dans le modal de
     * qualification — transmis tel quel à la file, jusqu'au POST /outcome.
     */
    contactId?: string
    /**
     * POSTE-PROMESSE (#353) : montant promis (« Promesse de don » = INTERESTED
     * + montant) — transporté par la file jusqu'au POST /outcome, clé omise
     * quand absent.
     */
    pledgedAmount?: number
  }): void
  /** L'agent renonce à qualifier — le poste redevient disponible. */
  abandonWrapup(): void
  /**
   * Disponibilité TELLE QU'ÉCRITE en base par la v2 (`null` tant que rien n'a
   * été publié). Sert au badge, qui doit montrer l'état réel, pas une intention.
   */
  availability: boolean | null
  /**
   * Bascule manuelle de la disponibilité (badge cliquable). Le choix de l'agent
   * survit aux ré-enregistrements du poste jusqu'à la fin de la session.
   */
  toggleAvailability(): void
  /**
   * PAUSE-PLATEAU : `true` quand le serveur dit que MON plateau est en pause
   * (`GET /tenants/me/options`, relu toutes les 60 s et au retour de
   * visibilité ; passe à `true` dès un 403 `TENANT_PAUSED` reçu en composant).
   * Le poste reste enregistré : le serveur, lui, ne fait plus sonner personne.
   */
  tenantPaused: boolean
}

const TelephonyContext = createContext<TelephonyApi | null>(null)

/** Store externe déjà fourni par le Device : aucune copie d'état à maintenir. */
const subscribeDeviceStatus = (listener: () => void) =>
  telephonyDevice.subscribe('status', listener)
const getDeviceStatus = () => telephonyDevice.getStatus()
const subscribeDeviceVolume = (listener: () => void) =>
  telephonyDevice.subscribe('volume', listener)
const getDeviceVolume = () => telephonyDevice.getVolume()

export function TelephonyProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const enabled = isTelephonyV2Enabled()

  /**
   * Dernière disponibilité ÉCRITE par la v2. Évite de repousser la même valeur
   * à chaque événement de statut du poste (un `registered` peut se répéter :
   * reprise de veille, re-register réseau).
   */
  const lastAvailabilityRef = useRef<boolean | null>(null)

  /**
   * Choix MANUEL de l'agent (badge cliquable). Prime sur la valeur déduite de
   * l'état du poste : sans lui, un simple ré-enregistrement (reprise de veille,
   * reprise réseau) remettait « Disponible » derrière le dos d'un agent qui
   * venait de se déclarer indisponible. Vidé au démontage du provider.
   */
  const manualAvailabilityRef = useRef<boolean | null>(null)

  /** Vue réactive de la dernière disponibilité publiée (pour le badge). */
  const [availability, setAvailability] = useState<boolean | null>(null)

  /**
   * PAUSE-PLATEAU — état de MON plateau, tel que le SERVEUR le dit.
   *
   * Pourquoi un sondage et pas un événement socket : le canal `/telephony`
   * (gelé) ne porte aucun événement « tenant » aujourd'hui — en ajouter un
   * exigerait d'injecter le gateway téléphonie dans le module tenants (hors
   * gel) et d'étendre le client socket gelé. Une relecture toutes les 60 s
   * (borne du lot), plus une au retour de visibilité (iPad qui se réveille)
   * et un passage immédiat à `true` sur tout 403 `TENANT_PAUSED`, suffisent :
   * c'est le serveur qui refuse, l'écran ne fait que le dire. Une erreur de
   * lecture CONSERVE l'état connu — une coupure réseau n'affiche jamais une
   * pause qui n'existe pas.
   */
  const [tenantPaused, setTenantPaused] = useState(false)
  useEffect(() => {
    if (!enabled || !user) return
    let cancelled = false
    const refresh = async () => {
      try {
        const options = await telephonyApi.getTenantOptions()
        if (!cancelled) setTenantPaused(options.canMakeCalls === false)
      } catch (error) {
        console.warn('[telephony-v2] État du plateau non lu — état conservé', error)
      }
    }
    void refresh()
    const timer = window.setInterval(() => void refresh(), TENANT_OPTIONS_POLL_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
      setTenantPaused(false)
    }
  }, [enabled, user?.id, user?.tenantId])

  /**
   * L'observateur de FIN D'APPEL vit ici, et pas dans les listes : le provider
   * est le seul composant qui ne se démonte jamais, donc le seul qui voie
   * passer la transition pendant que la porte manuelle, elle, est démontée.
   * Cf. `useCallEndRefresh` — il n'affiche rien et ne charge rien.
   */
  useCallEndRefresh(enabled)

  /**
   * F-5 — la disponibilité en base doit refléter l'état RÉEL du poste.
   *
   * Constat de recette : `isAvailable` restait à `true` des jours après la
   * déconnexion d'un agent. Le routage entrant lui envoyait des appels qui
   * sonnaient 25 s dans le vide avant de finir en manqué.
   *
   * On réutilise l'endpoint EXISTANT du CRM (`PATCH /users/me/availability`,
   * celui du badge v1) : l'agent ne peut modifier que sa propre disponibilité,
   * l'identité vient du JWT. Aucun nouvel endpoint, aucune permission nouvelle.
   */
  const publishAvailability = useCallback(async (isAvailable: boolean) => {
    if (lastAvailabilityRef.current === isAvailable) return
    lastAvailabilityRef.current = isAvailable
    setAvailability(isAvailable)
    try {
      await telephonyApi.setAvailability(isAvailable)
    } catch (error) {
      // Ne jamais bloquer la téléphonie sur ce point : au pire la base est
      // en retard d'un cran, ce qui est déjà le comportement d'avant ce lot.
      // On oublie la valeur écrite pour que la prochaine occasion réessaie.
      lastAvailabilityRef.current = null
      setAvailability(null)
      console.warn('[telephony-v2] Disponibilité non publiée', error)
    }
  }, [])

  /**
   * M5 — bascule manuelle depuis le badge. L'agent doit pouvoir se déclarer
   * indisponible sans quitter son poste (le badge v1 le permettait ; le badge
   * v2 ne le permettait plus, ce qui était une régression fonctionnelle une
   * fois le flag actif).
   *
   * Le choix est MÉMORISÉ : le prochain `registered` du poste (reprise de
   * veille, reprise réseau) le respecte au lieu de le réécrire en « dispo ».
   */
  const toggleAvailability = useCallback(() => {
    if (lastAvailabilityRef.current === null) {
      // La dernière publication a ÉCHOUÉ : la base et l'écran ne sont plus
      // d'accord, et personne ne sait qui a raison. Le clic RESYNCHRONISE
      // (il republie l'intention connue) au lieu de basculer à l'aveugle vers
      // une valeur qu'on croirait être l'inverse de l'actuelle.
      void publishAvailability(manualAvailabilityRef.current ?? true)
      return
    }
    const next = !lastAvailabilityRef.current
    manualAvailabilityRef.current = next
    void publishAvailability(next)
  }, [publishAvailability])

  // --- Cycle de vie Device + socket -----------------------------------------
  useEffect(() => {
    if (!enabled || !user) return

    let cancelled = false

    /**
     * REPRISE des qualifications laissées par une session précédente de CET
     * agent (mineur c de la review de #47) : onglet fermé ou PWA rechargée
     * pendant qu'une écriture volait. Le provider est le seul à connaître
     * l'utilisateur, donc le seul à pouvoir décider ce qui lui appartient.
     */
    outcomeQueue.hydrate(user.id)

    // La disponibilité suit l'ENREGISTREMENT réel du poste, pas le montage du
    // provider : un Device qui perd sa session (`unregistered`) rend l'agent
    // injoignable immédiatement.
    /**
     * Issue #190 — la socket s'ouvre au PREMIER enregistrement du poste, d'où
     * qu'il vienne. Elle n'était ouverte que dans le `.then` de l'init du
     * chargement : un poste mis en service plus tard (clic « Activer le
     * micro », reprise après autorisation) serait resté sans `call.updated`.
     * Une seule ouverture par montage — les reconnexions restent l'affaire de
     * socket.io, on ne recrée rien à chaque ré-enregistrement (réveil iPad).
     */
    let socketStarted = false
    const startSocket = () => {
      if (cancelled || socketStarted) return
      socketStarted = true
      telephonySocket.connect({ tenantId: user.tenantId, userId: user.id })
    }

    const unsubscribeStatus = telephonyDevice.subscribe('status', (status) => {
      if (cancelled) return
      if (status.state === 'registered') {
        startSocket()
        // Poste opérationnel : joignable, SAUF si l'agent s'est déclaré
        // indisponible à la main (son choix n'est pas écrasé par un
        // ré-enregistrement). Un poste rétabli après erreur (M1) repasse ici :
        // c'est ce qui rend l'agent à nouveau joignable sans rechargement.
        void publishAvailability(manualAvailabilityRef.current ?? true)
      } else if (
        status.state === 'unregistered' ||
        status.state === 'error' ||
        // Issue #190 : en attente du geste « Activer le micro », aucun poste
        // n'est enregistré — l'agent n'est pas joignable, la base doit le dire.
        status.state === 'awaiting-mic'
      ) {
        void publishAvailability(false)
      }
    })

    /**
     * REPRISE DE LA QUALIFICATION après rafraîchissement (issue #220, C3).
     *
     * La machine vit en mémoire : un rechargement la remet à `idle`, alors que
     * le serveur garde le `Call` ANSWERED + PENDING et compte l'agent en
     * wrapup 180 s (entrants suivants en file puis MISSED). On demande donc
     * au serveur s'il reste à CET agent un appel à qualifier et, si oui, la
     * machine repasse en `wrapup` : la scène (ou la barre d'appel, hors scène)
     * propose la qualification. Ce wrapup se quitte comme tout wrapup v2 : par
     * une issue (un appel est TOUJOURS qualifié — l'UI n'a pas de bouton
     * « Ignorer »), par la préemption d'un entrant, ou par l'API d'abandon
     * (`abandonWrapup`, sans appelant UI).
     *
     * Après l'init du poste, quelle qu'en soit l'issue (`finally`) : la
     * qualification ne dépend ni du micro ni du Device. Jamais bloquant — une
     * lecture en échec laisse le poste au repos, comme avant ce lot. Et la
     * machine garde le dernier mot : un entrant présenté pendant la lecture
     * n'est jamais écrasé (`restoreWrapup` refuse hors du repos).
     */
    const restorePendingWrapup = async () => {
      let pending: PendingWrapup | null
      try {
        pending = await telephonyApi.getPendingWrapup()
      } catch (error) {
        console.warn('[telephony-v2] Appel à qualifier non relu — reprise ignorée', error)
        return
      }
      if (cancelled || !pending) return
      const endedAt = Date.parse(pending.endedAt)
      const restored = callMachine.restoreWrapup({
        callId: pending.callId,
        direction: pending.direction,
        phoneNumber: pending.phoneNumber,
        contactId: pending.contactId,
        contactName: pending.contactName,
        campaignId: pending.campaignId,
        campaignContactId: pending.campaignContactId,
        durationSeconds: pending.duration,
        endedAt: Number.isFinite(endedAt) ? endedAt : Date.now(),
      })
      if (restored) {
        console.warn('[telephony-v2] Qualification reprise après rechargement', {
          callId: pending.callId,
        })
      }
    }

    telephonyDevice
      .init()
      .then(() => {
        if (cancelled) return
        // Issue #190 : l'init a pu s'arrêter AVANT le micro (pré-écran). Rien
        // n'est enregistré : la socket attendra le premier `registered`.
        if (telephonyDevice.getStatus().state === 'awaiting-mic') return
        startSocket()
      })
      .catch((error) => {
        console.error('[telephony-v2] Échec init Device', error)
        // Poste hors service = agent injoignable. Le dire à la base est ce qui
        // évite les 25 s de sonnerie dans le vide chez l'appelant.
        void publishAvailability(false)
      })
      .finally(() => {
        if (cancelled) return
        void restorePendingWrapup()
      })

    return () => {
      cancelled = true
      // LOT-BLINDAGE (B5) — cleanup DURCI : un throw de `unsubscribeStatus()`
      // ou de `telephonySocket.disconnect()` ne doit JAMAIS sauter
      // `telephonyDevice.destroy()`. Sans cette garde, un Device Twilio
      // orphelin resterait enregistré après démontage (double sonnerie au
      // remontage, agent fantôme joignable). Chaque étape est isolée : la
      // suivante s'exécute quoi qu'il arrive.
      try {
        unsubscribeStatus()
      } catch (error) {
        console.error('[telephony-v2] cleanup: unsubscribeStatus a jeté', error)
      }
      try {
        telephonySocket.disconnect()
      } catch (error) {
        console.error('[telephony-v2] cleanup: telephonySocket.disconnect a jeté', error)
      }
      try {
        telephonyDevice.destroy()
      } catch (error) {
        console.error('[telephony-v2] cleanup: telephonyDevice.destroy a jeté', error)
      }
      callMachine.reset()
      // Poste démonté (déconnexion, changement d'utilisateur, fermeture) :
      // l'agent n'est plus joignable. Requête « au mieux » — un onglet tué net
      // ne la passera pas, c'est le rôle de la garde anti-orphelins et du
      // routage de rattraper ces cas.
      lastAvailabilityRef.current = null
      manualAvailabilityRef.current = null
      // La file en MÉMOIRE est détachée (déconnexion, changement d'agent) ;
      // ce qui est persisté RESTE, et sera repris si cet agent revient dans
      // cet onglet. Rien n'est perdu, rien ne fuit vers l'autre.
      outcomeQueue.detach()
      void telephonyApi.setAvailability(false).catch(() => undefined)
    }
    // `publishAvailability` est stable (useCallback sans dépendance).
  }, [enabled, user?.id, user?.tenantId, publishAvailability])

  // --- Actions ---------------------------------------------------------------

  /**
   * Séquence d'appel sortant — l'ORDRE est le contrat (constats F-4 et F-6).
   *
   *  1. NORMALISER en E.164. Un « 06 12 34 56 78 » composé par un agent était
   *     refusé sec par le DTO backend (400).
   *  2. POST /telephony/calls. En cas d'échec on s'ARRÊTE : aucun
   *     `device.connect()` ne doit partir sans Call, sinon Twilio place un
   *     appel que le CRM ne connaît pas.
   *  3. `device.connect()`. En cas d'échec, ANNULER le Call créé à l'étape 2 :
   *     laissé en `QUEUED` sans `twilioSid`, il fait passer l'agent pour
   *     occupé et la file d'attente cesse de dépiler vers lui.
   */
  /**
   * ÉTAPE 1 — création du `Call`, SANS composer.
   *
   * Extraite de `dial()` pour le pré-lancement : c'est exactement le morceau
   * qui coûte un aller-retour réseau. Aucun leg Twilio n'existe à la sortie
   * de cette fonction ; le `Call` créé est donc annulable sans conséquence
   * (`discardPrepared`).
   */
  const prepareCall = useCallback(
    async (params: DialParams): Promise<PrepareResult> => {
      // Le contrat de `enabled` dit « faux ⇒ toutes les actions sont inertes » —
      // il n'était appliqué nulle part. Sans cette garde, une page appelant
      // `dial()` flag OFF créait un `Call` en base qu'aucun poste ne composerait
      // (aucun Device n'est initialisé), donc un orphelin de plus.
      if (!enabled) return { ok: false, reason: 'disabled' }

      const normalized = normalizePhoneToE164(params.phoneNumber)
      if (!normalized.isValid || !normalized.normalized) {
        // Le détail technique (quel format a échoué) part au journal ; l'agent,
        // lui, n'a besoin que de savoir que le numéro n'est pas composable.
        console.warn('[telephony-v2] Numéro non normalisable', {
          input: params.phoneNumber,
          detail: normalized.error,
        })
        return { ok: false, reason: 'invalid-number' }
      }

      // `preview` est un point de départ LÉGITIME : c'est l'état d'où part
      // l'auto-dial (rebours en cours, rebours échu, « Appeler maintenant »).
      // Les autres phases signifient qu'une ligne est déjà ouverte.
      const phase = callMachine.getSnapshot().phase
      if (phase !== 'idle' && phase !== 'preview') {
        return { ok: false, reason: 'busy' }
      }

      try {
        const created = await telephonyApi.createCall({
          toNumber: normalized.normalized,
          contactId: params.contactId,
          campaignId: params.campaignId,
          campaignContactId: params.campaignContactId,
        })
        return {
          ok: true,
          prepared: {
            callId: created.callId,
            phoneNumber: normalized.normalized,
            // LOT-SCÈNE-LINK : le serveur a pu RÉSOUDRE le contact du numéro
            // composé (rattachement auto de la PR #118 — match unique ou
            // rien). La scène apprend enfin cette résolution : le nom du
            // contact s'affiche au lieu de « Contact inconnu », et le
            // `contactId` suit tout le fil (wrapup, actions rapides, bouton
            // « nouveau contact » qui se retire de lui-même). Ce que
            // l'appelant SAVAIT déjà (porte campagne) garde la priorité.
            contactId: params.contactId ?? created.contact?.id,
            contactName: params.contactName ?? created.contact?.name,
            campaignId: params.campaignId,
            campaignContactId: params.campaignContactId,
          },
        }
      } catch (error) {
        // PAUSE-PLATEAU : le serveur refuse parce que le plateau est en pause.
        // Pas une panne, pas un toast rouge : l'état passe à « en pause » tout
        // de suite (sans attendre la prochaine relecture) et la scène affiche
        // son bandeau. Aucun `Call` n'existe, rien à annuler.
        if (readTenantPaused(error)) {
          console.warn('[telephony-v2] Plateau en pause — appel refusé par le serveur')
          setTenantPaused(true)
          return { ok: false, reason: 'tenant-paused' }
        }
        // LOT-INDICATIFS-FIX-2 : la DESTINATION est refusée au plan de
        // numérotation (PR #129). Ce n'est pas une panne : le diagnostic du
        // serveur remonte tel quel à la scène, qui l'affiche à l'agent.
        const refusal = readInvalidDestination(error)
        if (refusal) {
          console.warn('[telephony-v2] Destination refusée par le serveur', {
            input: params.phoneNumber,
            e164: normalized.normalized,
            ...refusal,
          })
          return {
            ok: false,
            reason: 'invalid-destination',
            message: refusal.message,
            hint: refusal.hint,
          }
        }
        console.error('[telephony-v2] Création du Call refusée', error)
        return { ok: false, reason: 'create-failed' }
      }
    },
    [enabled],
  )

  /**
   * ÉTAPE 2 — composer un appel préparé.
   *
   * En cas d'échec, ANNULER le `Call` de l'étape 1 : laissé en `QUEUED` sans
   * `twilioSid`, il fait passer l'agent pour occupé et la file d'attente
   * cesse de dépiler vers lui (constat F-6).
   */
  const dialPrepared = useCallback(
    async (prepared: PreparedCall): Promise<DialResult> => {
      callMachine.clearError()

      const started = callMachine.startOutbound({
        callId: prepared.callId,
        phoneNumber: prepared.phoneNumber,
        contactId: prepared.contactId,
        contactName: prepared.contactName,
        // Portés dans la machine, donc lisibles pendant TOUTE la conversation :
        // c'est ce qui permet d'afficher le script de la campagne sans que la
        // page n'ait à mémoriser quoi que ce soit.
        campaignId: prepared.campaignId,
        campaignContactId: prepared.campaignContactId,
      })
      if (!started) {
        // Un entrant s'est glissé entre la préparation et ici : le Call préparé
        // n'aura jamais lieu, il ne doit pas rester à traîner.
        await telephonyApi.cancelCall(prepared.callId).catch(() => undefined)
        return { ok: false, reason: 'busy' }
      }

      try {
        await telephonyDevice.connect({ callId: prepared.callId })
        return { ok: true, callId: prepared.callId }
      } catch (error) {
        console.error('[telephony-v2] device.connect() en échec', error)
        // #144 (E-09) : le poste est la SEULE source de cause pour cet appel
        // sans SID — le code SDK part avec l'annulation, le serveur le classe
        // et rend le libellé décliné (crédit épuisé ≠ micro refusé).
        const sdkError = readSdkError(error)
        const cancelled = await telephonyApi
          .cancelCall(prepared.callId, sdkError)
          .catch((cancelError) => {
            // La garde anti-orphelins du routage entrant prend le relais.
            console.warn('[telephony-v2] Annulation du Call impossible', cancelError)
            return null
          })
        // Même garde de phase que `submitOutcome` : `connect()` et l'annulation
        // ont pu prendre du temps, et un ENTRANT peut avoir été présenté
        // entre-temps. Un `fail()` inconditionnel le ferait disparaître de
        // l'écran — exactement le bug F-8 qu'on vient de fermer ailleurs.
        const after = callMachine.getSnapshot()
        if (after.callId === prepared.callId) {
          // CLÉ i18n, pas une phrase (cf. `CallState.error`). Avec une cause
          // classée par le serveur, la clé est celle de la catégorie ; sinon le
          // message générique historique.
          const category = cancelled?.failure?.category
          if (category) {
            callMachine.fail(`dialFailure.cause.${category}`, cancelled?.failure?.code)
          } else {
            callMachine.fail('dialError.connect-failed')
          }
        } else {
          console.warn(
            '[telephony-v2] Échec de connexion, mais le poste a changé d’état entre-temps — état conservé',
            { failedCallId: prepared.callId, phase: after.phase, callId: after.callId },
          )
        }
        return { ok: false, reason: 'connect-failed' }
      }
    },
    [],
  )

  /**
   * Le `Call` préparé ne sera pas composé (Pause, Stop, entrant, changement de
   * porte).
   *
   * Route DÉDIÉE, et non `cancelCall` : celle-ci **conserve la réservation du
   * contact** (il reste présenté et sera composé à la reprise) et **supprime**
   * le `Call` jamais composé au lieu de le clore en échec. Voir
   * `api.abortPrelaunch` — c'est la double retouche M-2/M-3.
   *
   * « Au mieux » : un abandon perdu laisse un `Call` que la garde
   * anti-orphelins et le nettoyage rattrapent — jamais un appel réel.
   */
  const discardPrepared = useCallback((prepared: PreparedCall) => {
    void telephonyApi.abortPrelaunch(prepared.callId).catch((error) => {
      console.warn('[telephony-v2] Abandon du Call préparé impossible', error)
    })
  }, [])

  /**
   * RAPPEL D'UN LEG EN VOL — Stop/Pause dans la fenêtre de connexion anticipée
   * (constat C de la recette du 2026-07-29 : « stop à T-1 laisse partir
   * l'appel »).
   *
   * L'ORDRE est le contrat, et il est l'inverse du chemin nominal :
   *
   *  1. **la MACHINE d'abord** — `abortDialing()` porte toutes les gardes
   *     (phase `dialing` seulement, sortant, jamais décroché, contact de
   *     campagne). Si elle refuse, on n'a rien raccroché : un appel qui sonne
   *     déjà, ou une conversation, ne se rembobine pas ;
   *  2. **le POSTE ensuite** — raccrochage SILENCIEUX du leg : ses écouteurs
   *     sont retirés avant, donc son `disconnect` ne peut pas rouvrir un
   *     wrapup par-dessus la préview qu'on vient de ré-armer ;
   *  3. **le SERVEUR enfin**, « au mieux » : le `Call` est supprimé (aucune
   *     fiche fantôme) et la réservation CONSERVÉE. Une requête perdue laisse
   *     un `Call` que la garde anti-orphelins rattrape — jamais un appel réel,
   *     puisque le leg est déjà raccroché.
   */
  const abortLaunchedCall = useCallback(
    (params: { callId: string; countdownSeconds: number }): boolean => {
      if (!callMachine.abortDialing({ countdownSeconds: params.countdownSeconds })) {
        return false
      }
      telephonyDevice.abortDialing()
      void telephonyApi.abortLaunch(params.callId).catch((error) => {
        console.warn('[telephony-v2] Rappel du leg non transmis au serveur', error)
      })
      return true
    },
    [],
  )

  /**
   * Séquence d'appel sortant COMPLÈTE — l'ORDRE est le contrat (F-4 et F-6) :
   * normaliser, créer le `Call`, puis seulement composer.
   */
  const dial = useCallback(
    async (params: DialParams): Promise<DialResult> => {
      callMachine.clearError()
      const prepared = await prepareCall(params)
      // Le refus voyage ENTIER (raison + diagnostic serveur éventuel).
      if (!prepared.ok) return prepared
      return dialPrepared(prepared.prepared)
    },
    [prepareCall, dialPrepared],
  )

  /**
   * QUALIFICATION OPTIMISTE — l'avancée ne coûte plus un aller-retour réseau.
   *
   * L'ordre est le contrat, et il est l'INVERSE de celui d'avant :
   *
   *  1. on lit ce qu'il faut pour rejouer l'écriture (identifiant du `Call`,
   *     ligne de campagne, nom du correspondant) TANT QUE la machine le sait —
   *     elle va être remise au repos à la ligne suivante ;
   *  2. **la machine est libérée TOUT DE SUITE.** C'est ce geste qui fait
   *     apparaître le contact suivant : il est déjà chargé et verrouillé,
   *     `useCampaignDialer` n'a qu'à armer la préview dessus ;
   *  3. l'écriture est DÉPOSÉE dans `outcomeQueue`, qui la sérialise et
   *     signale un échec éventuel — sans jamais interrompre l'appel suivant.
   *
   * ⚠️ LA GARDE DE PHASE RESTE (M3). Un entrant peut avoir été présenté entre
   * le clic et ce code (l'agent tape, le téléphone sonne) : un `reset()`
   * inconditionnel l'avalerait — écran vide, appelant qui sonne dans le vide
   * jusqu'au manqué. Elle est simplement évaluée AVANT le réseau au lieu
   * d'après, ce qui referme au passage une fenêtre qui durait tout le POST.
   */
  const submitOutcome = useCallback(
    (params: {
      call?: QualifiedCall | null
      outcome: string
      notes?: string
      scheduledCallbackTime?: string
      contactId?: string
      pledgedAmount?: number
    }): void => {
      const snapshot = callMachine.getSnapshot()
      const target: QualifiedCall | null =
        params.call ??
        (snapshot.callId
          ? {
              callId: snapshot.callId,
              campaignContactId: snapshot.campaignContactId,
              label:
                snapshot.contactName || snapshot.phoneNumber || snapshot.callId,
            }
          : null)

      /**
       * La DÉCISION est une fonction pure (`outcomeSubmission.ts`) : on dépose
       * toujours, on ne libère que si la machine désigne encore CET appel.
       * Cf. son en-tête pour le mineur b de la review de #47 — refuser tout
       * quand un entrant s'intercale PERDAIT la qualification saisie.
       */
      const plan = planOutcomeSubmission({
        phase: snapshot.phase,
        currentCallId: snapshot.callId,
        targetCallId: target?.callId ?? null,
      })

      if (plan.enqueue && target) {
        outcomeQueue.enqueue({
          callId: target.callId,
          outcome: params.outcome,
          notes: params.notes,
          scheduledCallbackTime: params.scheduledCallbackTime,
          campaignContactId: target.campaignContactId,
          label: target.label,
          // LOT-SCÈNE-LINK : fiche créée/reprise en qualification — la file la
          // transporte, le POST ne porte la clé que lorsqu'elle existe.
          contactId: params.contactId,
          // POSTE-PROMESSE : même règle pour le montant promis.
          pledgedAmount: params.pledgedAmount,
        })
      }

      if (plan.releaseMachine) {
        callMachine.reset()
      } else if (plan.enqueue) {
        console.warn(
          '[telephony-v2] Résultat DÉPOSÉ, mais le poste a changé d’état — état conservé',
          {
            qualifiedCallId: target?.callId,
            phase: snapshot.phase,
            callId: snapshot.callId,
          },
        )
      }
    },
    [],
  )

  /**
   * ABANDON de qualification — LIBÉRATION RÉELLE.
   *
   * L'ancienne version ne libérait que la machine locale : côté serveur le
   * `Call` restait `ANSWERED`+`PENDING`, donc l'agent restait exclu du routage
   * entrant jusqu'au bout de la fenêtre de wrapup et la file d'attente
   * n'avançait pas. On prévient donc le backend (qui clôt l'outcome, trace la
   * raison en `systemNote` et relance le dépilage), en symétrie exacte avec
   * l'outcome et l'annulation.
   *
   * L'état local est libéré TOUT DE SUITE, sans attendre la réponse : le bouton
   * « Ignorer » est justement ce dont l'agent dispose pour se débloquer — le
   * rendre dépendant du réseau reviendrait à le lui retirer quand il en a le
   * plus besoin. En cas d'échec, la garde anti-wrapup du routing et le
   * nettoyage restent les filets.
   */
  const abandonWrapup = useCallback(() => {
    const { callId, phase } = callMachine.getSnapshot()
    callMachine.abandonWrapup()
    if (phase !== 'wrapup' || !callId) return
    void telephonyApi.abandonWrapup(callId).catch((error) => {
      console.warn('[telephony-v2] Abandon non transmis au serveur', error)
    })
  }, [])

  const api = useMemo<TelephonyApi>(
    () => ({
      enabled,
      dial,
      prepareCall,
      dialPrepared,
      discardPrepared,
      abortLaunchedCall,
      submitOutcome,
      acceptIncoming: () => telephonyDevice.acceptIncoming(),
      rejectIncoming: () => telephonyDevice.rejectIncoming(),
      hangup: () => telephonyDevice.hangup(),
      toggleMute: () =>
        telephonyDevice.setMuted(!callMachine.getSnapshot().isMuted),
      sendDigits: (digits: string) => telephonyDevice.sendDigits(digits),
      setVolume: (level: number) => telephonyDevice.setVolume(level),
      // Inerte flag OFF, comme toutes les actions (aucun poste n'existe).
      requestMicrophone: () =>
        enabled ? telephonyDevice.requestMicrophone() : Promise.resolve(),
      abandonWrapup,
      availability,
      toggleAvailability,
      tenantPaused,
    }),
    [
      enabled,
      dial,
      prepareCall,
      dialPrepared,
      discardPrepared,
      abortLaunchedCall,
      submitOutcome,
      abandonWrapup,
      availability,
      toggleAvailability,
      tenantPaused,
    ],
  )

  return (
    <TelephonyContext.Provider value={api}>
      {children}
      {/* Les entrants sont présentés depuis ICI, donc sur TOUTE page — c'est
          la raison d'être d'un Device global. Rendu seulement flag actif :
          sans lui, l'overlay n'existe pas dans l'arbre v1. */}
      {enabled && <IncomingCallOverlay />}
      {/* Une qualification en échec suit l'agent PARTOUT, pour la même raison :
          il a peut-être quitté la scène entre-temps, et le résultat d'un appel
          déjà passé ne doit pas se perdre parce qu'il a changé d'écran. */}
      {enabled && <PendingOutcomeBanner />}
      {/* Issue #220 : l'appel engagé (et le wrapup) suivent l'agent sur TOUTE
          page autre que la scène — raccrocher, ouvrir la scène, qualifier.
          Sans elle, un entrant décroché hors scène n'avait ni panneau ni
          « Raccrocher » ; la barre se tait d'elle-même sur /v2/softphone. */}
      {enabled && <ActiveCallBar />}
    </TelephonyContext.Provider>
  )
}

/** Actions de téléphonie v2. Lève si utilisé hors du provider (erreur de câblage). */
export function useTelephony(): TelephonyApi {
  const context = useContext(TelephonyContext)
  if (!context) {
    throw new Error('useTelephony doit être utilisé dans <TelephonyProvider>')
  }
  return context
}

/** Hook de lecture de l'état d'appel — vue réactive sur la machine. */
export function useCallState(): CallState {
  return useSyncExternalStore(callMachine.subscribe, callMachine.getSnapshot)
}

/** État du poste (enregistré, micro refusé, session expirée…) — pour le badge. */
export function useDeviceStatus(): TelephonyDeviceStatus {
  return useSyncExternalStore(subscribeDeviceStatus, getDeviceStatus)
}

/**
 * VOLUME D'ÉCOUTE — vue réactive sur le poste, à l'usage du SEUL contrôle.
 *
 * ⚠️ Il n'a rien à faire dans un `useState` de page (mineur e de la review de
 * #47) : chaque cran du curseur re-rendrait alors la scène ENTIÈRE — fiche,
 * script, onglets — pendant que l'agent parle. Ici, seul le composant qui
 * affiche le curseur s'abonne, exactement comme le chronomètre pour le tic.
 */
export function useDeviceVolume(): number {
  return useSyncExternalStore(subscribeDeviceVolume, getDeviceVolume)
}
